import React, { useState } from "react";
import { connect } from "react-redux";
import { sendClaim } from "@actions/claims";
import { defaultAction } from "@actions/defaultAction";
import { sendSupportMsg } from "@actions/contact";
import {
  TOGGLE_ACCOUNT_HELP_BOX,
  SET_CLAIM_TYPE,
  SET_CLAIM_AMOUNT_TO_REFUND
} from "@constants/actionTypes";
import { Field, reduxForm, getFormValues, getFormSyncErrors } from "redux-form";
import {
  issue,
  secondaryIssue,
  subject,
  validateClaimAmount,
  message
} from "@components/validation/supportCardLevelForm";
import RenderFormFieldNew from "@components/forms/RenderFormFieldNew";
import styled from "@emotion/styled";
import RenderDropdownHelp from "@components/RenderDropdownHelp";
import CustomSubmitButton from "./CustomSubmitButton";
import {
  CARD_ISSUES_WITHOUT_CLAIM,
  CARD_ISSUES_WITH_CLAIM,
  MISSING_INFO_OPTIONS,
  CARD_NUMBER_INVALID_OPTIONS,
  BALANCE_DISCREPANCY_OPTIONS,
  NOT_ACCEPTED_BY_MERCHANT_OPTIONS
} from "@constants/customerSupport";

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const FirstInputWrapper = styled(InputWrapper)`
  z-index: 3;
`;

const ButtonContainer = styled.div`
  margin-top: 45px;
`;

const ServerError = styled.div`
  color: #ee5050;
  margin-bottom: 14px;
`;

// NOTE: There must be only one instance of a specific type of validator.
//       As per issue #2453 in Redux Form, must create single instance to
//       prevent memory leaks (creating new function every time form is rendered)
//       **DO NOT CREATE INSTANCE IN RENDER METHOD**
const validateClaim = (claimAmount, _, props) => {
  const { orderAssistance } = props;
  return validateClaimAmount(
    claimAmount,
    orderAssistance.prevClaimAmount,
    orderAssistance.faceValue
  );
};

const validateSecondaryIssue = (value, props) => {
  return secondaryIssue(value, props);
};

const SupportCardLevelForm = ({
  handleSubmit,
  hideModal,
  orderAssistance,
  formData,
  setClaimType,
  sendSupportMessage,
  sendClaim,
  setClaimAmountToRefund,
  invalid,
  change,
  formErrors,
  setFinished
}) => {
  const [serverError, setServerError] = useState();
  const submit = async fields => {
    const supportBase = {
      name: orderAssistance.name,
      email: orderAssistance.email,
      orderId: orderAssistance.orderId,
      orderType: orderAssistance.orderType
    };

    if (
      formData &&
      (formData.issue?.display === "Other" ||
        formData.issue?.display === "Did not receive my card")
    ) {
      const supportResponse = await sendSupportMessage({
        ...supportBase,
        subject: fields.subject ? fields.subject : formData.issue?.display,
        message: fields.message || "no additional message"
      });
      if (supportResponse.error) {
        setServerError(supportResponse.error);
      } else if (supportResponse === "success") {
        setFinished(true);
      }
      return;
    }

    let claimValue;
    if (formData.issue?.display === "Card missing information") {
      claimValue =
        Number(orderAssistance.faceValue) -
        Number(orderAssistance.prevClaimAmount);
    } else {
      claimValue = Number(fields.claimAmount);
    }
    const claimResponse = await sendClaim({
      ...supportBase,
      subject: `${formData.issue.display} | ${formData.secondaryIssue.display}`,
      message: fields.message || "no additional message",
      merchantName: orderAssistance.merchantName,
      cardId: orderAssistance.fulfilledId,
      faceValue: Number(orderAssistance.faceValue),
      cardType: orderAssistance.cardType,
      claimValue: claimValue,
      previousClaimValue: 0,
      claimType: Number(orderAssistance.claimType)
    });
    if (claimResponse.error) {
      setServerError(claimResponse.error);
    } else if (claimResponse === "success") {
      setFinished(true);
    }
  };

  let cardIssue = null;
  let primaryDropdown;
  let secondaryDropdownOptions;
  let secondaryDropdownDefault;

  let reachedClaimMax = false;

  if (orderAssistance.prevClaimAmount >= Number(orderAssistance.faceValue)) {
    reachedClaimMax = true;
  }

  if (formData) {
    cardIssue = formData.issue?.display ? formData.issue.display : null;
  }

  switch (cardIssue) {
    case "Card missing information":
      secondaryDropdownOptions = MISSING_INFO_OPTIONS;
      secondaryDropdownDefault = "What's missing?";
      break;
    case "Card number invalid":
      secondaryDropdownOptions = CARD_NUMBER_INVALID_OPTIONS;
      secondaryDropdownDefault = "Where did you attempt to use it?";
      break;
    case "Balance discrepancy":
      secondaryDropdownOptions = BALANCE_DISCREPANCY_OPTIONS;
      secondaryDropdownDefault = "Where did you attempt to use it?";
      break;
    case "Not accepted by merchant":
      secondaryDropdownOptions = NOT_ACCEPTED_BY_MERCHANT_OPTIONS;
      secondaryDropdownDefault = "Where did you attempt to use it?";
      break;
  }

  const handleChange = e => {
    setClaimType(e.id);
    change("secondaryIssue", null);
  };

  const calculateClaimAmountToRefund = e => {
    const cost = orderAssistance.cost;
    const faceValue = orderAssistance.faceValue;
    const claimPercentage = Number(e.target.value) / faceValue;
    const claimAmountToRefund = parseFloat(cost * claimPercentage).toFixed(2);

    if (/^\d+(,\d{3})*(\.\d*)?$/.test(claimAmountToRefund)) {
      setClaimAmountToRefund(claimAmountToRefund);
    }
  };

  if (!orderAssistance.fulfilledId || reachedClaimMax) {
    primaryDropdown = CARD_ISSUES_WITHOUT_CLAIM;
  } else {
    primaryDropdown = CARD_ISSUES_WITH_CLAIM;
  }

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="remove-number-input-arrows"
    >
      <FirstInputWrapper>
        <Field
          name="issue"
          component={RenderDropdownHelp}
          list={primaryDropdown}
          addArrow={true}
          onChange={handleChange}
          defaultValue={"Select Issue"}
          validate={issue}
        />
      </FirstInputWrapper>
      {secondaryDropdownOptions ? (
        <InputWrapper>
          <Field
            name="secondaryIssue"
            list={secondaryDropdownOptions}
            component={RenderDropdownHelp}
            addArrow={true}
            defaultValue={secondaryDropdownDefault}
            validate={validateSecondaryIssue}
          />
        </InputWrapper>
      ) : null}
      {cardIssue === "Other" ? (
        <InputWrapper>
          <Field
            name="subject"
            component={RenderFormFieldNew}
            type="text"
            placeholder="Subject"
            validate={subject}
          />
        </InputWrapper>
      ) : null}
      {cardIssue === "Balance discrepancy" ||
      cardIssue === "Card number invalid" ||
      cardIssue === "Not accepted by merchant" ? (
        <InputWrapper>
          <Field
            name="claimAmount"
            component={RenderFormFieldNew}
            validate={[validateClaim]}
            onChange={calculateClaimAmountToRefund}
            type="number"
            placeholder={
              globalThis?.innerWidth && globalThis?.innerWidth > 350
                ? "Please enter amount missing from card"
                : "Enter missing amount"
            }
            claimField
            orderAssistance={orderAssistance}
          />
          {!!orderAssistance.refundAmount &&
            formData.claimAmount &&
            !formErrors.claimAmount && (
              <div className="claim-refund-amt mar-t-3">{`Upon approval, the refund for this claim will be $${orderAssistance.refundAmount}.`}</div>
            )}
        </InputWrapper>
      ) : null}
      <InputWrapper>
        <Field
          name="message"
          component={RenderFormFieldNew}
          inputType={"textArea"}
          inputHeight={"200px"}
          type="text"
          placeholder="Please describe your issue in detail so we can assist you."
          topPlaceholder="Additional message"
          validate={message}
        />
      </InputWrapper>

      <ButtonContainer>
        {serverError && <ServerError>{serverError}</ServerError>}
        <CustomSubmitButton
          type="submit"
          styles={{ height: "72px", borderRadius: "36px" }}
          buttonType={"ORANGE_BUTTON"}
          appearDisabled={invalid}
        >
          Submit
        </CustomSubmitButton>
      </ButtonContainer>
    </form>
  );
};

export default connect(
  state => ({
    formData: getFormValues("supportCardLevel")(state),
    orderAssistance: state.orderAssistance,
    formErrors: getFormSyncErrors("supportCardLevel")(state)
  }),
  dispatch => ({
    hideModal: () => dispatch(defaultAction(TOGGLE_ACCOUNT_HELP_BOX)),
    setClaimType: id => dispatch(defaultAction(SET_CLAIM_TYPE, id)),
    setClaimAmountToRefund: refundAmount =>
      dispatch(defaultAction(SET_CLAIM_AMOUNT_TO_REFUND, refundAmount)),
    sendSupportMessage: payload => dispatch(sendSupportMsg(payload)),
    sendClaim: payload => dispatch(sendClaim(payload))
  })
)(
  reduxForm({
    form: "supportCardLevel",
    enableReinitialize: true
  })(SupportCardLevelForm)
);
